/**
 * Цветовые фильтры: аффинное преобразование RGB (матрица 3×3 и смещение по каналам) в sRGB.
 * Одна формула для превью (SVG feColorMatrix) и ролика (FFmpeg colorchannelmixer + colorlevels).
 */
export const COLOR_FILTERS = ['off', 'mistletoe', 'deepcyan', 'fading', 'y2k', 'kodak500t', 'warm', 'cool', 'soft', 'mono'] as const;
export type ColorFilter = (typeof COLOR_FILTERS)[number];
export const COLOR_FILTER_LABELS: Record<ColorFilter, string> = {
  off: 'Оригинал', mistletoe: 'Омела', deepcyan: 'Глубокий циан', fading: 'Меркнущий свет', y2k: 'Мода Y2K',
  kodak500t: 'Кодак 500T', warm: 'Тёплый', cool: 'Прохладный', soft: 'Мягкий', mono: 'Ч/б',
};

interface Grade { m: number[]; o: number[] }
const IDENTITY = [1, 0, 0, 0, 1, 0, 0, 0, 1];
const ZERO = [0, 0, 0];
const LUMA = [0.2126, 0.7152, 0.0722];

/** Шаги сборки образа; применяются по порядку: then(a, b) — сначала a, потом b. */
const matrix = (m: number[]): Grade => ({ m, o: ZERO });
const saturation = (s: number) => matrix([0, 1, 2].flatMap(r => [0, 1, 2].map(c => LUMA[c]! * (1 - s) + (r === c ? s : 0))));
/** Контраст вокруг середины: c > 1 — жёстче, c < 1 — мягче и с приподнятыми тенями. */
const contrast = (c: number): Grade => ({ m: [c, 0, 0, 0, c, 0, 0, 0, c], o: [0.5 * (1 - c), 0.5 * (1 - c), 0.5 * (1 - c)] });
const gains = (r: number, g: number, b: number) => matrix([r, 0, 0, 0, g, 0, 0, 0, b]);
/** Сдвиг уровней по каналам: плюс — приподнять тени этим цветом. */
const lift = (r: number, g: number, b: number): Grade => ({ m: IDENTITY, o: [r, g, b] });
function then(...steps: Grade[]): Grade {
  return steps.reduce((a, b) => ({
    m: [0, 1, 2].flatMap(r => [0, 1, 2].map(c => [0, 1, 2].reduce((sum, k) => sum + b.m[r * 3 + k]! * a.m[k * 3 + c]!, 0))),
    o: [0, 1, 2].map(r => [0, 1, 2].reduce((sum, k) => sum + b.m[r * 3 + k]! * a.o[k]!, 0) + b.o[r]!),
  }), matrix(IDENTITY));
}

const GRADES: Record<ColorFilter, Grade> = {
  off: matrix(IDENTITY),
  // Омела (как в CapCut): яркий контрастный образ — густой цвет, сочная тёплая кожа, светлые
  // тона без желтизны, глубокие тени с лёгким пурпурным отливом.
  mistletoe: then(saturation(1.3), contrast(1.22), gains(1.0, 0.97, 1.05), lift(0.01, -0.008, 0.016)),
  // Глубокий циан: тёплые тона уходят, бирюза и синева гуще, контраст выше.
  deepcyan: then(matrix([0.78, 0.14, 0.08, 0.02, 1.0, 0.06, 0.0, 0.16, 0.98]), gains(0.84, 1.03, 1.12), contrast(1.1), saturation(1.12), lift(0, 0.012, 0.024)),
  // Меркнущий свет: выцветшая плёнка — мягкий контраст, тёплые приподнятые тени, меньше цвета.
  fading: then(saturation(0.66), contrast(0.74), lift(0.035, 0.02, 0), gains(1.0, 0.97, 0.92)),
  // Мода Y2K: яркий и насыщенный, с холодным розово-синим отливом.
  y2k: then(saturation(1.38), gains(1.05, 0.96, 1.1), contrast(1.06), lift(0.012, 0, 0.02)),
  // Кодак 500T: киноплёнка под лампы накаливания — холодный общий тон, бирюзовые тени, тёплая кожа.
  kodak500t: then(matrix([1.06, 0.0, -0.06, -0.02, 1.0, 0.02, -0.04, 0.1, 0.94]), gains(0.96, 1.0, 1.06), saturation(0.88), contrast(0.92), lift(0, 0.012, 0.03)),
  warm: matrix([1.04, 0.024, 0, 0.008, 0.984, 0.008, 0, 0.032, 0.904]),
  cool: matrix([0.92, 0.04, 0, 0, 1, 0.008, 0.016, 0.024, 1.04]),
  soft: matrix([0.84, 0.128, 0.032, 0.064, 0.904, 0.032, 0.064, 0.128, 0.808]),
  mono: matrix([0.2126, 0.7152, 0.0722, 0.2126, 0.7152, 0.0722, 0.2126, 0.7152, 0.0722]),
};

/** Фильтр с учётом интенсивности: 0 — оригинал, 100 — полный образ. */
export function colorGrade(preset: ColorFilter = 'off', intensity = 70): Grade {
  const amount = Number.isFinite(intensity) ? Math.min(100, Math.max(0, intensity)) / 100 : 0;
  const grade = GRADES[preset];
  return {
    m: grade.m.map((v, i) => Number((IDENTITY[i]! + (v - IDENTITY[i]!) * amount).toFixed(6))),
    o: grade.o.map(v => Number((v * amount).toFixed(6))),
  };
}
export function colorMatrix(preset: ColorFilter = 'off', intensity = 70): number[] {
  return colorGrade(preset, intensity).m;
}
export function svgColorMatrix(preset: ColorFilter, intensity: number): string {
  const { m, o } = colorGrade(preset, intensity);
  return [m.slice(0, 3), [o[0]!, 0], m.slice(3, 6), [o[1]!, 0], m.slice(6, 9), [o[2]!, 0, 0, 0, 0, 1, 0]].flat().join(' ');
}
/**
 * colorchannelmixer не умеет смещений, поэтому их даёт colorlevels: плюс — выходная точка
 * чёрного (romin), минус — входная (rimin). Строки матрицы масштабируются так, чтобы
 * вместе с уровнями получилось ровно m·v + o, как в превью.
 */
export function ffmpegColorFilter(preset: ColorFilter = 'off', intensity = 70): string | null {
  if (preset === 'off' || intensity <= 0) return null;
  const { m, o } = colorGrade(preset, intensity);
  const names = ['rr', 'rg', 'rb', 'gr', 'gg', 'gb', 'br', 'bg', 'bb'];
  const scale = o.map(v => v > 0 ? 1 / (1 - v) : v < 0 ? 1 / (1 - v) : 1);
  const mixer = 'colorchannelmixer=' + m.map((v, i) => `${names[i]}=${Number((v * scale[Math.floor(i / 3)]!).toFixed(6))}`).join(':');
  if (o.every(v => v === 0)) return mixer;
  const levels = ['r', 'g', 'b'].map((c, i) => {
    const v = o[i]!;
    if (v > 0) return `${c}omin=${v.toFixed(6)}`;
    if (v < 0) return `${c}imin=${(-v / (1 - v)).toFixed(6)}`;
    return null;
  }).filter(Boolean).join(':');
  // 16 бит между шагами: иначе уровни растягивают округление смешивания до 8 бит.
  return `format=gbrp16le,${mixer},colorlevels=${levels}`;
}
